import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchInventory, type InventoryGift,withdrawGift } from './api';

type State = {
  gifts: InventoryGift[];
  loading: boolean;
  error: string | null;
  withdrawing: string | null; // gift id currently being withdrawn
};

export function useGiftInventory() {
  const [state, setState] = useState<State>({
    gifts: [],
    loading: false,
    error: null,
    withdrawing: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const gifts = await fetchInventory();
      setState((s) => ({ ...s, gifts, loading: false }));
    } catch (err: unknown) {
      if ((err as { name?: string }).name === 'CanceledError') return;
      setState((s) => ({ ...s, loading: false, error: 'Failed to load inventory' }));
    }
  }, []);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const withdraw = useCallback(async (giftId: string): Promise<boolean> => {
    setState((s) => ({ ...s, withdrawing: giftId }));
    try {
      await withdrawGift(giftId);
      setState((s) => ({
        ...s,
        withdrawing: null,
        gifts: s.gifts.filter((g) => g.id !== giftId),
      }));
      return true;
    } catch {
      setState((s) => ({ ...s, withdrawing: null }));
      return false;
    }
  }, []);

  const getFiltered = useCallback(
    (search: string): InventoryGift[] => {
      if (!search.trim()) return state.gifts;
      const q = search.toLowerCase();
      return state.gifts.filter((g) => g.name.toLowerCase().includes(q));
    },
    [state.gifts],
  );

  return {
    loading: state.loading,
    error: state.error,
    withdrawing: state.withdrawing,
    reload: load,
    withdraw,
    getFiltered,
  };
}
